import { IApiBaseProps } from '@common/api.models';
import { DEFAULT_SELECT_PAGE_SIZE } from '@common/constants';
import { Label, Select, IOption } from '@gcl/react-core';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';
import { OptionFilter, IFilterInputBaseProps, OptionsFilter, IOptionFilter } from '../searchFilter.models';
import { getFilterValue } from '../searchFilter.services';

export interface ISelectFilterInputProps extends IFilterInputBaseProps<OptionFilter | OptionsFilter> {}

export const SelectFilterInput: React.FunctionComponent<ISelectFilterInputProps> = ({
  fieldConfig,
  onChange,
  filter = {},
  disabled = false,
}) => {
  const [localValue, setLocalValue] = React.useState<IOption<any>[] | undefined>(undefined);
  const {
    fieldName,
    displayName,
    useFetchOptions,
    useFetchOptionsProps,
    optionPageSize,
    allowMultipleSelect,
    disable,
    selectGroupOptionByKey,
    selectDisplayFieldName,
    selectKeyFieldName,
  } = fieldConfig;
  const useFetchOptionsPlaceHolder = (apiBaseProps: IApiBaseProps) => {
    return useQuery([apiBaseProps.projectId, 'useFetchOptionsPlaceHolder'], async () => {
      return [] as IOption<any>[];
    });
  };

  const { data, isLoading } = (useFetchOptions || useFetchOptionsPlaceHolder)(useFetchOptionsProps);

  useDeepCompareEffectNoCheck(() => {
    const value = getFilterValue(fieldName, filter);
    if (!value) {
      setLocalValue(undefined);
      return;
    }

    if (!allowMultipleSelect) {
      const optionFilter = value as IOptionFilter;

      if (optionFilter.value) {
        setLocalValue([{ key: optionFilter.id, text: optionFilter.value }]);
        return;
      }

      const foundSelected = data?.find((d) => (d.key ?? d['id' as keyof typeof d]) === optionFilter.id);
      if (foundSelected) {
        const displayText = foundSelected[(selectDisplayFieldName ?? 'text') as keyof typeof foundSelected];
        setLocalValue([{ key: optionFilter.id, text: displayText || '' }]);
        return;
      }

      setLocalValue([{ key: optionFilter.id, text: '' }]);
      return;
    }

    const optionsFilter = value as IOptionFilter[];
    setLocalValue(
      optionsFilter.map((of) => {
        return { key: of.id, text: of.value };
      }),
    );
  }, [JSON.stringify(filter), JSON.stringify(data)]);

  return (
    <>
      <Label labelFor={fieldName}>{displayName}</Label>
      <Select
        multiple={!!allowMultipleSelect}
        value={localValue || undefined}
        pageSize={optionPageSize ?? DEFAULT_SELECT_PAGE_SIZE}
        displayFieldName={selectDisplayFieldName || 'text'}
        options={data || []}
        minSearchQueryLength={0}
        disabled={disabled || disable}
        onChange={(selected) => {
          if (!selected || selected.length <= 0) {
            setLocalValue(undefined);
            onChange(fieldName, undefined);
            return;
          }
          setLocalValue(selected);

          if (!allowMultipleSelect) {
            const selectedFirst = selected[0];
            const val = selectedFirst[(selectDisplayFieldName ?? 'text') as keyof typeof selectedFirst];
            onChange(fieldName, {
              id: selectedFirst.key ?? selectedFirst['id' as keyof typeof selectedFirst],
              value: (val ?? '').toString(),
            });
            return;
          }

          onChange(
            fieldName,
            selected.map((s) => {
              return { id: s.key, value: s.text.toString() };
            }),
          );
        }}
        id={fieldName}
      />
    </>
  );
};
